import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type {
  VincularRequest, ReTramiteRequest, AutoVincularRequest, VerificarRequest,
} from '@/types';

// Cola de bootstrap CFIA: grupos PorVincular con sus candidatos de lote.
// staleTime corto porque es trabajo CONCURRENTE (varios operadores; cuando
// uno vincula un grupo desaparece para los demas, 9.1) — conviene que se
// refresque seguido.
export function useBootstrapPorVincular() {
  return useQuery({
    queryKey: ['bootstrap-por-vincular'],
    queryFn: () => api.bootstrap.porVincular(),
    staleTime: 15 * 1000,
  });
}

// Buscador manual de lotes (escape de 9.4): para los grupos sin candidato y
// para "elegir otro lote". El query queda disabled hasta 2+ caracteres (el
// backend igual lo cortaria). El caller pasa el termino ya debounceado.
export function useBuscarLotes(q: string) {
  const term = q.trim();
  return useQuery({
    queryKey: ['bootstrap-buscar-lotes', term],
    queryFn: () => api.bootstrap.buscarLotes(term),
    enabled: term.length >= 2,
    staleTime: 30 * 1000,
  });
}

// Vincular un grupo a un lote. Al exito INVALIDA la bandeja en vez de parchear
// optimistamente: el grupo vinculado ya no esta PorVincular, asi que el refetch
// lo saca. Es lo correcto para concurrencia (9.1) — el refetch trae la verdad
// del server (si otro operador movio algo en paralelo, se refleja), en vez de
// un estado local que podria divergir. El manejo del 409 de re-tramite lo hace
// el componente leyendo el ApiError (status 409 + body.tramiteVigente).
export function useVincular() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VincularRequest) => api.bootstrap.vincular(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bootstrap-por-vincular'] });
    },
  });
}

// Re-tramite (camino explicito del 9.6). Al exito invalida la bandeja (el grupo
// desaparece). NO cierra el dialogo aca a proposito: el cierre lo dispara el
// componente en su onSuccess por-llamada, asi onError deja el dialogo ABIERTO
// mostrando el error adentro (concurrencia 9.1 — dos operadores sobre el mismo
// lote es donde mas duele el re-tramite, el operador debe ver que paso).
export function useReTramite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReTramiteRequest) => api.bootstrap.reTramite(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bootstrap-por-vincular'] });
    },
  });
}

// ============================================================
// B4: auto-vinculo + cola de verificacion.
// ============================================================

// Auto-vinculo con salvaguarda. NO invalida nada en dryRun (no escribio nada,
// y el resultado del conteo lo guarda el componente para mostrarlo). En la
// corrida real invalida las DOS listas: la bandeja se vacia de los grupos
// auto-vinculados y la cola de verificacion se llena con ellos.
export function useAutoVincular() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AutoVincularRequest) => api.bootstrap.autoVincular(body),
    onSuccess: res => {
      if (res.dryRun) return;
      qc.invalidateQueries({ queryKey: ['bootstrap-por-vincular'] });
      qc.invalidateQueries({ queryKey: ['bootstrap-por-verificar'] });
    },
  });
}

// Cola de verificacion (9.5). Mismo staleTime corto que la bandeja: tambien es
// trabajo concurrente (varios revisores sobre la misma cola).
export function useBootstrapPorVerificar() {
  return useQuery({
    queryKey: ['bootstrap-por-verificar'],
    queryFn: () => api.bootstrap.porVerificar(),
    staleTime: 15 * 1000,
  });
}

// Confirmar / rechazar un auto-vinculado. Invalida las dos listas porque
// "rechazar" devuelve los correos a la bandeja: el grupo REAPARECE alla.
export function useVerificar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VerificarRequest) => api.bootstrap.verificar(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bootstrap-por-verificar'] });
      qc.invalidateQueries({ queryKey: ['bootstrap-por-vincular'] });
    },
  });
}
